import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import styled from 'styled-components';
import { operationType } from '../../../../services/types';
import { validJSON } from '../../../../services/method';

export function Input({ id, data }: NodeProps<operationType>) {
   const { setNodes } = useReactFlow();
   const textareaRef = React.useRef(null);

   // Format output value for display
   const displayValue = React.useMemo(() => {
      if (data.action === 'output') {
         if (typeof data.value === 'object' && data.value !== null) {
            return JSON.stringify(data.value, null, 2);
         }
         return data.value;
      }
      return data.value;
   }, [data.value, data.action]);

   // Detect Image
   const imageURL = React.useMemo(() => {
      const fileData = data.action === 'output' ? validJSON(data.value) : null;
      return fileData && fileData.type?.startsWith('image') ? fileData.url : null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value]);

   // Detect audio URL from output value (S3 TTS URLs or audio file URLs)
   const audioURL = React.useMemo(() => {
      if (data.action !== 'output') return null;
      const value = data.value;

      // Check if it's a direct audio URL string
      if (typeof value === 'string') {
         if (value.includes('/tts/') && value.endsWith('.wav')) return value;
         if (value.match(/\.(wav|mp3|ogg|webm)$/i)) return value;
      }

      // Check if it's a JSON object with audio type
      const fileData = validJSON(value);
      if (fileData && fileData.type?.startsWith('audio')) return fileData.url;

      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value]);
   // ---
   React.useLayoutEffect(() => {
      const textarea = textareaRef.current;

      if (!imageURL && textareaRef.current) { // @ts-ignore
         textarea.style.height = 'auto'; // @ts-ignore
         textarea.style.height = `${textarea.scrollHeight + 4}px`;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value]);

   // ---
   const onChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNodes((nds) =>
         nds.map((node) => {
            if (node.id === id) return { ...node, data: { ...node.data, value: e.target.value } };
            else return node;
         })
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   return (
      <Main className={data.action}>{
         audioURL ? (
            // Audio player for TTS output
            <div className="audio-container">
               <audio controls src={audioURL} style={{ width: '100%', minWidth: '250px' }}>
                  Your browser does not support the audio element.
               </audio>
            </div>
         ) : (
            <textarea
               style={{
                  backgroundImage: `url(${imageURL})`,
                  ...(imageURL ? { width: '500px', height: '300px' } : null)
               }}
               ref={textareaRef}
               value={imageURL ? '' : displayValue}
               onChange={onChange}
               disabled={imageURL || data.disabled || data.action === 'output'}
               className={!imageURL ? `nodrag nowheel ${data.action === 'input' && !data.value ? 'noValue' : ''}` : ''}
            ></textarea>
         )
      }</Main>
   );
}

// ---
const Main = styled.div`
   min-width: 230px;

   &.output textarea {
      min-width: 250px;
      border: none;
   }
   textarea {
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      background-color: #F6F6F6;
      border: 1px dashed rgb(200, 200, 200);
      padding: 5px 7.5px;
      font-size: 16px;
      border-radius: 10px;
      min-width: 100%;
      max-width: 500px;
      min-height: 60px;
      max-height: 300px;
      resize: both !important;
   }
   .noValue {
      border: 1px solid #F05454;
   }
   .audio-container {
      background-color: #F6F6F6;
      border-radius: 10px;
      padding: 10px;
      min-width: 250px;
      
      audio {
         border-radius: 8px;
         outline: none;
      }
   }
`;